import React, { useState } from "react";
import { createTheme, ThemeProvider, TextField } from "@mui/material";
import { styled } from "@mui/system";
import { toTitleCase } from "../../helpers/string";
import { SurveyMainType } from "../../types/SurveyMainType";
import { SurveyQuestionType } from "../../types/SurveyQuestionType";
import useWindowDimensions from "../../hooks/useWindowDimensions";

interface ISurveySelectProps {
  question: SurveyQuestionType;
  pppData?: React.Dispatch<React.SetStateAction<SurveyMainType | undefined>>;
  errorIds?: number[];
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-outlined.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(0.75) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiInputLabel-shrink, & fieldset": {
    fontSize: "13px",
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 30,
  },
}));

const StyledTextFieldPep = styled(StyledTextField)({
  width: "100%",
});

const SurveySelect = ({ question, pppData, errorIds }: ISurveySelectProps) => {
  //const { height, width } = useWindowDimensions();
  const [value, setValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const [otherValue, setOtherValue] = useState<string>("");

  if (pppData == null) return null;

  return (
    <ThemeProvider theme={theme}>
      <div className="hrMargin" />

      <p>{question.text}</p>
      <div id={`Question-${question.id}`}>
        {question.answers &&
          question.answers.map((c) => {
            return (
              <div
                key={c.id}
                onClick={() => {
                  pppData((prev) => {
                    const q = prev?.questions.find((f) => f.id === question.id);

                    if (q == null || q.answers == null) return prev;
                    const a = q.answers.find((f) => f.id === c.id);
                    if (a?.isSelected) return prev;

                    q.isTouched = true;
                    q.answerValueText = undefined;
                    q.answers.forEach((ans) => {
                      ans.isSelected = false;
                    });

                    if (a == null) return prev;

                    a.isSelected = true;
                    a.additionalText = undefined;
                    if (prev) return { ...prev };
                  });
                  setValue(c.answerText);
                }}
                className="custom-control custom-checkbox"
              >
                <input
                  value={c.answerText}
                  type="checkbox"
                  className="custom-control-input"
                  checked={c.isSelected}
                />
                <label className="custom-control-label">{c.answerText}</label>

                {c.isSelected && c.showTextBox ? (
                  <div className="form-group mt-3 mb-3">
                    <StyledTextFieldPep
                      variant="outlined"
                      value={
                        question?.answers?.find((f) => f.id === c.id)
                          ?.additionalText
                      }
                      label={c.additionalTextBoxLabel ?? ""}
                      error={errorIds && errorIds.indexOf(question.id) !== -1}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={(event) => {
                        const val = toTitleCase(event.target.value);
                        pppData((prev) => {
                          const q = prev?.questions.find(
                            (f) => f.id === question.id
                          );
                          if (q == null || q.answers == null) return prev;

                          q.isTouched = true;

                          const a = q.answers.find((f) => f.id === c.id);
                          if (a == null) return prev;
                          a.additionalText = val;

                          if (prev) return { ...prev };
                        });
                        setOtherValue(val);
                      }}
                      inputProps={{
                        maxLength: 200,
                      }}
                      InputLabelProps={{
                        style: {
                          fontSize:
                            c.additionalTextBoxLabel?.length > 40 ? 9 : 13,
                          marginTop:
                            c.additionalTextBoxLabel?.length > 40 &&
                            (isFocused ||
                              question?.answers?.find((f) => f.id === c.id)
                                ?.additionalText)
                              ? -12
                              : 0,
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                        },
                      }}
                      InputProps={{
                        style: {
                          height: "50px",
                        },
                      }}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                    />
                    {errorIds &&
                      errorIds.indexOf(question.id) !== -1 &&
                      question.answers?.some(
                        (item) => item.isSelected && item.showTextBox
                      ) && (
                        <span className="validationError" role="alert">
                          <i className="fa fa-exclamation-circle" />
                          To pole jest wymagane
                        </span>
                      )}
                  </div>
                ) : null}
              </div>
            );
          })}
        {errorIds &&
          errorIds.indexOf(question.id) !== -1 &&
          question.answers?.filter(
            (item) => item.isSelected && item.showTextBox
          ).length === 0 && (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              To pole jest wymagane
            </span>
          )}
      </div>
    </ThemeProvider>
  );
};

export { SurveySelect };
